'use client'

// IMPORTANT: Import appkit-init FIRST to ensure createAppKit runs before any hooks
import '@/lib/appkit-init'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WagmiProvider, type State } from 'wagmi'
import { SessionProvider } from 'next-auth/react'
import { wagmiAdapter } from './wagmi-config'
import { TronProvider } from './tron-provider'
import { TonProvider } from './ton-provider'
import { ReactNode, useState, useEffect } from 'react'

interface Web3ProviderProps {
    children: ReactNode
    initialState?: State
}

// Safety net: wagmi's connector calls toChecksummedAddress(accounts[0]) when a
// mobile wallet fires accountsChanged([]) on disconnect. accounts[0] is undefined,
// causing an unhandled "Cannot read properties of undefined (reading 'toLowerCase')"
// rejection. This is benign (the wallet IS disconnecting) but pollutes error monitors.
// Fixed upstream in wagmi ≥ 3.6.16 / viem ≥ 2.50 — this guard covers cached bundles.
function useWagmiDisconnectErrorGuard() {
    useEffect(() => {
        const handler = (event: PromiseRejectionEvent) => {
            const msg: string = event.reason?.message ?? String(event.reason ?? '')
            if (
                msg.includes("Cannot read properties of undefined (reading 'toLowerCase')") ||
                msg.includes('toChecksummedAddress')
            ) {
                // Suppress — this is a harmless wagmi connector internal error on wallet disconnect
                event.preventDefault()
            }
        }
        window.addEventListener('unhandledrejection', handler)
        return () => window.removeEventListener('unhandledrejection', handler)
    }, [])
}

export function Web3Provider({ children, initialState }: Web3ProviderProps) {
    const [queryClient] = useState(() => new QueryClient())
    useWagmiDisconnectErrorGuard()

    return (
        <SessionProvider refetchOnWindowFocus={false} refetchInterval={5 * 60}>
            <WagmiProvider config={wagmiAdapter.wagmiConfig} initialState={initialState}>
                <QueryClientProvider client={queryClient}>
                    <TronProvider>
                        <TonProvider>
                            {children}
                        </TonProvider>
                    </TronProvider>
                </QueryClientProvider>
            </WagmiProvider>
        </SessionProvider>
    )
}
